<template>
  <div v-if="isLoading" class="flex h-[60vh] justify-center">
    <LoadingIcon :isLoading="isLoading" />
  </div>
  <div class="flex flex-col gap-4" v-if="resource && !isLoading">
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-2">
        <h1 class="font-extrabold text-2xl">
          {{ t("resources.detail.title") }}
        </h1>
        <div class="text-light-gray text-sm">
          {{ resource.name }}
        </div>
      </div>
      <CustomButton
        @click="go(RouteName.Resources)"
        :title="t('resources.detail.returnToResourcesPage')"
        buttonStyles="bg-white! text-[#64748B]! h-[50%]"
      />
    </div>
    <div class="grid grid-cols-4 gap-3 my-4">
      <InfoBox
        :title="t('common.name')"
        :value="resource.name"
        :backgroundColor="EventState.events[2].lightColor"
        :textColor="EventState.events[2].color"
      >
        <IconManager
          name="Pencil"
          :style="{ color: EventState.events[2].color }"
        />
      </InfoBox>

      <InfoBox
        :title="t('common.type')"
        :value="resource!.type"
        :backgroundColor="EventState.events[4].lightColor"
        :textColor="EventState.events[4].color"
      >
        <IconManager
          name="Check"
          :style="{ color: EventState.events[4].color }"
        />
      </InfoBox>

      <InfoBox
        :title="t('common.quantity')"
        :value="resource!.maxQuantity"
        :backgroundColor="EventState.events[0].lightColor"
        :textColor="EventState.events[0].color"
      >
        <IconManager
          name="Box"
          :style="{ color: EventState.events[0].color }"
        />
      </InfoBox>

      <InfoBox
        :title="t('resources.detail.price')"
        :value="Utils.formatCompactPrice(resource.cost, locale)"
        :backgroundColor="EventState.events[3].lightColor"
        :textColor="EventState.events[3].color"
      >
        <IconManager
          name="Euro"
          :style="{ color: EventState.events[3].color }"
        />
      </InfoBox>
    </div>

    <div class="flex flex-row gap-4">
      <FileUploader
        :title="t('resources.detail.resourceTypeImage')"
        v-model:file="uploadedImage"
        :existing-url="storedImageUrl"
        :saveCallBack="updateImage"
        accept="image/jpeg,image/png,image/webp"
        :deleteCallBack="deleteImage"
        :buttonIsLoading="imageIsLoading"
      />

      <div class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full">
        <div class="resource-info-row">
          <span class="title">{{ t("common.name") }}</span>
          <span class="value">{{ resource.name }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("common.type") }}</span>
          <span class="value">{{ resource.type }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("common.quantity") }}</span>
          <span class="value">{{ resource.maxQuantity }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("resources.detail.assemblyTime") }}</span>
          <span class="value">{{ resource.assemblyTime }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("resources.detail.disassemblyTime") }}</span>
          <span class="value">{{ resource.disassemblyTime }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("resources.detail.price") }}</span>
          <span class="value">{{
            Utils.formatCompactPrice(resource.cost, locale)
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import FileUploader from "@/core/components/FileUploader.vue";
import "./ResourceDetailPage.css";
import { ResourceDetail } from "@/modules/resources/models/resource-detail";
import { useI18n } from "vue-i18n";
import { resourceRemoteDataSource } from "@/core/models/global-variables";
import { useRoute, useRouter } from "vue-router";
import CustomButton from "@/core/components/CustomButton.vue";
import { RouteName } from "@/core/models/route-name";
import { computed, onMounted, ref } from "vue";
import { EventState } from "@/modules/events/models/event-state";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import { Utils } from "@/core/models/utils";

defineProps<{
  id: string;
}>();

const { t, locale } = useI18n();
const { showError, showSuccess } = useToast();
const router = useRouter();
const route = useRoute();
const resource = ref<ResourceDetail | null>(null);
const isLoading = ref<boolean>(true);
const imageIsLoading = ref<boolean>(false);
const uploadedImage = ref<File | null>(null);

const storedImageUrl = computed(() =>
  resource.value?.imageVersion
    ? `${import.meta.env.VITE_API_URL}/Resources/${route.params.id}/image?w=480&v=${resource.value.imageVersion}`
    : null,
);

function go(name: string, params: Record<string, any> = {}) {
  router.push({
    name,
    params,
  });
}

onMounted(() => {
  loadInitialData();
});

async function loadInitialData() {
  isLoading.value = true;

  try {
    const response = await resourceRemoteDataSource.getResource(
      route.params.id as string,
    );
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }

    resource.value = response.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function updateImage() {
  if (!uploadedImage.value) return false;
  imageIsLoading.value = true;
  

  try {
    const response = await resourceRemoteDataSource.updateImage(
      route.params.id as string,
      { file: uploadedImage.value! },
    );
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
      return false;
    }
    if (resource.value) {
      resource.value.imageVersion = (response.data as any)?.imageVersion ?? null;
    }
    uploadedImage.value = null;
    showSuccess({ title: t("message.updatedSucess") });
    return true;
  } catch (error: any) {
    showError({ title: error });
    return false;
  } finally {
    imageIsLoading.value = false;
  }
}

async function deleteImage() {
  imageIsLoading.value = true;

  try {
    const response = await resourceRemoteDataSource.deleteImage(
      route.params.id as string,
    );
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
      return false;
    }

    if (resource.value) resource.value.imageVersion = null;

    showSuccess({ title: t("message.deletedSucess") });
    return true;
  } catch (error: any) {
    showError({ title: error });
    return false;
  } finally {
    imageIsLoading.value = false;
  }
}
</script>
